import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Upload,
  FileText,
  Video,
  Image,
  Music,
  Folder,
  Trash2,
  Edit3,
  Download,
  Eye,
  Lock,
  Loader2,
  X,
  Check,
  AlertCircle,
  Plus,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'course_module', label: 'Course Module', icon: Folder },
  { value: 'stationery', label: 'Stationery', icon: FileText },
  { value: 'guide', label: 'Guide', icon: FileText },
  { value: 'template', label: 'Template', icon: FileText },
  { value: 'bonus', label: 'Bonus Content', icon: FileText },
  { value: 'other', label: 'Other', icon: Folder }
];

const TIERS = [
  { value: 'free', label: 'Free (Everyone)', color: 'bg-stone-100 text-stone-700' },
  { value: 'pen_friend', label: 'Pen Friend', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'correspondent', label: 'Correspondent', color: 'bg-purple-100 text-purple-700' },
  { value: 'postmaster', label: 'Postmaster', color: 'bg-amber-100 text-amber-700' }
];

const FILE_ICONS = {
  pdf: FileText,
  video: Video,
  image: Image,
  audio: Music
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Upload Modal Component
const UploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('course_module');
  const [tierRequired, setTierRequired] = useState('free');
  const [moduleNumber, setModuleNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!title) setTitle(droppedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!title) setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!file || !title) {
      setError('Please select a file and enter a title');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('tier_required', tierRequired);
    if (moduleNumber) formData.append('module_number', moduleNumber);

    try {
      const response = await fetch(`${API_URL}/api/resources/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        setError(data.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-serif text-stone-800">Upload Resource</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-lws-green bg-lws-green/5' : 'border-stone-300 hover:border-stone-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.mp4,.webm,.mov,.jpg,.jpeg,.png,.webp,.gif,.mp3,.wav,.ogg"
            />
            {file ? (
              <div className="space-y-2">
                <Check className="w-10 h-10 text-lws-green mx-auto" />
                <p className="font-medium text-stone-800">{file.name}</p>
                <p className="text-sm text-stone-500">{formatFileSize(file.size)}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 text-stone-400 mx-auto" />
                <p className="text-stone-600">Drop a file here or click to browse</p>
                <p className="text-xs text-stone-400">PDF, Video, Image, Audio (max 500MB for video)</p>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Module 1: Getting Started"
              className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-lws-green focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the resource..."
              rows={2}
              className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-lws-green focus:border-transparent resize-none"
            />
          </div>

          {/* Category & Module */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Module #</label>
              <input
                type="number"
                value={moduleNumber}
                onChange={(e) => setModuleNumber(e.target.value)}
                placeholder="Optional"
                min="1"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          </div>

          {/* Tier Access */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Who can access?</label>
            <div className="grid grid-cols-2 gap-2">
              {TIERS.map(tier => (
                <button
                  key={tier.value}
                  onClick={() => setTierRequired(tier.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    tierRequired === tier.value 
                      ? 'border-lws-green bg-lws-green/10' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  {tier.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-stone-200 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button 
            onClick={handleUpload} 
            disabled={uploading || !file || !title}
            className="flex-1 bg-lws-green hover:bg-lws-green/90"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onDelete, onEdit }) => {
  const [deleting, setDeleting] = useState(false);
  const FileIcon = FILE_ICONS[resource.file_type] || FileText;
  const tierInfo = TIERS.find(t => t.value === resource.tier_required);

  const handleDelete = async () => {
    if (!window.confirm('Delete this resource? This cannot be undone.')) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/resources/admin/${resource.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        onDelete();
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-stone-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          resource.file_type === 'video' ? 'bg-blue-100 text-blue-600' :
          resource.file_type === 'image' ? 'bg-pink-100 text-pink-600' :
          resource.file_type === 'audio' ? 'bg-purple-100 text-purple-600' :
          'bg-stone-100 text-stone-600'
        }`}>
          <FileIcon className="w-6 h-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-stone-800 truncate">{resource.title}</h3>
            {resource.module_number && (
              <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded">
                Module {resource.module_number}
              </span>
            )}
          </div>
          
          <p className="text-sm text-stone-500 line-clamp-1">{resource.description || 'No description'}</p>
          
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${tierInfo?.color || 'bg-stone-100'}`}>
              {tierInfo?.label || resource.tier_required}
            </span>
            <span className="text-xs text-stone-400">{formatFileSize(resource.file_size)}</span>
            <span className="text-xs text-stone-400">{resource.download_count || 0} downloads</span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <a
            href={`${API_URL}/api/resources/download/${resource.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </a>
          <button
            onClick={() => onEdit(resource)}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Edit Modal Component
const EditModal = ({ resource, onClose, onSuccess }) => {
  const [title, setTitle] = useState(resource.title);
  const [description, setDescription] = useState(resource.description || '');
  const [category, setCategory] = useState(resource.category);
  const [tierRequired, setTierRequired] = useState(resource.tier_required);
  const [moduleNumber, setModuleNumber] = useState(resource.module_number || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('tier_required', tierRequired);
    if (moduleNumber) formData.append('module_number', moduleNumber);

    try {
      const response = await fetch(`${API_URL}/api/resources/admin/${resource.id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-serif text-stone-800">Edit Resource</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-stone-200 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-stone-200 rounded-lg resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Module #</label>
              <input
                type="number"
                value={moduleNumber}
                onChange={(e) => setModuleNumber(e.target.value)}
                min="1"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Access Tier</label>
            <div className="grid grid-cols-2 gap-2">
              {TIERS.map(tier => (
                <button
                  key={tier.value}
                  onClick={() => setTierRequired(tier.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    tierRequired === tier.value 
                      ? 'border-lws-green bg-lws-green/10' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  {tier.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-stone-200 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="flex-1 bg-lws-green hover:bg-lws-green/90"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default function AdminResourcesPage() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchResources = useCallback(async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API_URL}/api/resources/admin/list`
        : `${API_URL}/api/resources/admin/list?category=${selectedCategory}`;
      
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      
      setResources(data.resources || []);
      setCategoryCounts(data.category_counts || {});
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchResources();
    }
  }, [user, fetchResources]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Lock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-stone-600">Admin Access Required</h1>
        </div>
      </div>
    );
  }

  const totalResources = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-resources-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-lws-green to-lws-green/90 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Folder className="w-8 h-8" />
                <h1 className="text-3xl font-serif">Resources</h1>
              </div>
              <p className="text-white/80">Upload and manage PDFs, videos, and other course materials</p>
            </div>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-white text-lws-green hover:bg-white/90"
              data-testid="upload-resource-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Resource
            </Button>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-6 px-6 border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === 'all' 
                  ? 'bg-lws-green text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              All ({totalResources})
            </button>
            {CATEGORIES.map(cat => {
              const count = categoryCounts[cat.value] || 0;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat.value 
                      ? 'bg-lws-green text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources List */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
              <Upload className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600">No resources yet</h3>
              <p className="text-stone-400 mt-1 mb-4">
                {selectedCategory === 'all' 
                  ? 'Upload your first resource to get started.' 
                  : `No ${CATEGORIES.find(c => c.value === selectedCategory)?.label || selectedCategory} resources.`}
              </p>
              <Button onClick={() => setShowUpload(true)} className="bg-lws-green hover:bg-lws-green/90">
                <Plus className="w-4 h-4 mr-2" />
                Upload Resource
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {resources.map(resource => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onDelete={fetchResources}
                  onEdit={setEditingResource}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <UploadModal
            onClose={() => setShowUpload(false)}
            onSuccess={fetchResources}
          />
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingResource && (
          <EditModal
            resource={editingResource}
            onClose={() => setEditingResource(null)}
            onSuccess={fetchResources}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
